import { nodeValueOrder } from "../domain/nodeValueOrder";
import {
  Flag,
  FlagSeverity,
  Orientation,
  PATH_SEPARATOR,
  StudioDraft,
  StudioItem,
  SpineLevel,
  effectiveAttrs,
  isPlaceholderValue,
  levelsForScope,
} from "../domain/studioTypes";

export interface StudioTreeNode {
  id: string;
  parentId: string | null;
  scopeKey: string;
  /** Same as `scopeKey`. This walk needs two real values; the generator counts nulls. */
  orderScopeKey: string;
  attr: string;
  levelLabel: string;
  /** Drawn column: a skipped level skips a column so branches stay aligned. */
  depth: number;
  value: string;
  itemCount: number;
  orient: Orientation;
  flagCounts: Record<FlagSeverity, number>;
  childIds: string[];
  /** Do not use `spine[depth + 1]` — that can offer a rung no item carries. */
  branchLevels: readonly number[];
  /** Sibling move is an index into this list, not plan-wide `valueOrder`. */
  siblings: readonly string[];
  /** This branch's level sequence — not the global spine. */
  branchSpine: readonly SpineLevel[];
}

export interface StudioTree {
  nodesById: Record<string, StudioTreeNode>;
  rootIds: string[];
}

export const projectTree = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  flags: readonly Flag[],
): StudioTree => {
  const nodesById: Record<string, StudioTreeNode> = {};
  const rootIds: string[] = [];

  const flagsByScope = new Map<string, Flag[]>();
  for (const flag of flags) {
    const key = flag.scopePath.join(PATH_SEPARATOR);
    const list = flagsByScope.get(key) ?? [];
    list.push(flag);
    flagsByScope.set(key, list);
  }

  const flagCountsAt = (id: string): Record<FlagSeverity, number> => {
    const counts: Record<FlagSeverity, number> = { blocking: 0, toCheck: 0, toReview: 0 };
    for (const [scope, scopeFlags] of flagsByScope) {
      if (scope === id || scope.startsWith(id + PATH_SEPARATOR)) {
        for (const flag of scopeFlags) counts[flag.severity] += 1;
      }
    }
    return counts;
  };

  const attrsOf = new Map<string, Record<string, string | null>>();
  const attrs = (item: StudioItem): Record<string, string | null> => {
    const cached = attrsOf.get(item.skuId);
    if (cached) return cached;
    const resolved = effectiveAttrs(item, draft);
    attrsOf.set(item.skuId, resolved);
    return resolved;
  };

  const discriminates = (scopeItems: readonly StudioItem[], attr: string): boolean => {
    const seen = new Set<string>();
    for (const item of scopeItems) {
      const value = attrs(item)[attr] ?? "";
      if (isPlaceholderValue(value)) continue;
      seen.add(value);
      if (seen.size >= 2) return true;
    }
    return false;
  };

  const branchLevelsOf = (
    scopeItems: readonly StudioItem[],
    fromLevelIndex: number,
    path: readonly string[],
  ): number[] => {
    const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, path);
    const out: number[] = [];
    for (let index = fromLevelIndex; index < levels.length; index += 1) {
      if (discriminates(scopeItems, levels[index].attr)) out.push(index);
    }
    return out;
  };

  const walk = (
    scopeItems: readonly StudioItem[],
    levelIndex: number,
    path: readonly string[],
    parentId: string | null,
    depth: number,
  ) => {
    const levels = levelsForScope(draft.spine, draft.scopedLevelOrder, path);
    if (levelIndex >= levels.length || scopeItems.length <= 1) return;
    const level = levels[levelIndex];
    // Skip keeps the column for a level that does not split this branch.
    if (!discriminates(scopeItems, level.attr)) {
      walk(scopeItems, levelIndex + 1, path, parentId, depth + 1);
      return;
    }
    const groups = new Map<string, StudioItem[]>();
    for (const item of scopeItems) {
      const value = attrs(item)[level.attr] ?? "";
      const group = groups.get(value);
      if (group) group.push(item);
      else groups.set(value, [item]);
    }
    const scopeKey = path.join(PATH_SEPARATOR);
    const orderScopeKey = scopeKey;
    // Never the plan-wide pool alone.
    const want = nodeValueOrder(
      draft,
      [orderScopeKey, scopeKey],
      level.attr,
      scopeItems.map((item) => ({ value: attrs(item)[level.attr], placements: item.placements })),
    );
    const rank = new Map(want.map((value, index) => [value, index]));
    const ordered = [...groups.entries()].sort(
      (a, b) => (rank.get(a[0]) ?? want.length) - (rank.get(b[0]) ?? want.length),
    );
    const orient =
      draft.orientOverrides[orderScopeKey] ?? draft.orientOverrides[scopeKey] ?? level.orient;

    const siblings = ordered.map(([value]) => value);

    for (const [value, group] of ordered) {
      const id = [...path, value].join(PATH_SEPARATOR);
      const flagCounts = flagCountsAt(id);
      nodesById[id] = {
        id,
        parentId,
        scopeKey,
        orderScopeKey,
        attr: level.attr,
        levelLabel: level.label,
        depth,
        value,
        itemCount: group.length,
        orient,
        flagCounts,
        childIds: [],
        siblings,
        branchLevels: branchLevelsOf(group, levelIndex + 1, [...path, value]),
        branchSpine: levelsForScope(draft.spine, draft.scopedLevelOrder, [...path, value]),
      };
      if (parentId === null) rootIds.push(id);
      else nodesById[parentId].childIds.push(id);
      walk(group, levelIndex + 1, [...path, value], id, depth + 1);
    }
  };

  walk(items, 0, [], null, 0);
  return { nodesById, rootIds };
};
